'use client'

import {
  type ComponentProps,
  type RefObject,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { cn } from './style'

const getScrollMetrics = (ref?: RefObject<HTMLElement | null>) => {
  const el = ref?.current

  if (el) {
    return {
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight - el.clientHeight,
    }
  }

  return {
    scrollTop: window.scrollY,
    scrollHeight: document.documentElement.scrollHeight - window.innerHeight,
  }
}

type Props = ComponentProps<'div'> & {
  scrollContainerRef?: RefObject<HTMLElement | null>
}

export const ScrollProgressBar = ({
  className,
  scrollContainerRef,
  ...props
}: Props) => {
  const [progress, setProgress] = useState(0)

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight } = getScrollMetrics(scrollContainerRef)

    const scrollPercent = (scrollTop / scrollHeight) * 100

    const clampedProgress = Math.min(Math.max(scrollPercent, 0), 100)

    setProgress(clampedProgress)
  }, [scrollContainerRef])

  useEffect(() => {
    const target = scrollContainerRef?.current ?? window

    target.addEventListener('scroll', handleScroll)

    return () => {
      target.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, scrollContainerRef])

  return (
    <div className={cn('h-1 w-full bg-gray-100', className)} {...props}>
      <div
        className="h-1 w-full bg-purple-500"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}
